import { twMerge } from "tailwind-merge";
import { Button, Card, H2, InputPassword } from "@sellar-io/components";
import {
  confirmPasswordReset,
  signInWithEmailAndPassword,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
} from "firebase/auth";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { getContinueUrlFromOnboardingUrlStorage } from "../../../utils/sellarHelpers";
import { Mixpanel } from "../../../utils/mixpanel";

export function ResetPassword({ oobCode }: { oobCode: string }): ReactElement {
  const navigate = useNavigate();
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { auth } = useFirebaseUser();
  const [error, setError] = useState("");

  const [onboardingContinueUrl] = useLocalStorage("onboardingContinueUrl", "");

  useEffect(() => {
    void firebaseVerifyPasswordResetCode(auth, oobCode)
      .then(setEmail)
      .catch((error: unknown) => {
        setError(error instanceof Error ? error.message : "Unknown error");
        setDisabled(true);
      })
      .finally(() => setLoading(false));
  }, [auth, oobCode]);

  const handleResetPassword = async () => {
    try {
      setLoading(true);
      setError("");

      await confirmPasswordReset(auth, oobCode, password);
      await signInWithEmailAndPassword(auth, email, password);
      Mixpanel.track("Password Reset");

      navigate(getContinueUrlFromOnboardingUrlStorage(onboardingContinueUrl));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const renderErrorMessage = () => <Alert type={"error"} message={error} />;

  return (
    <>
      {loading ? (
        <SpinnerLegacy />
      ) : disabled ? (
        renderErrorMessage()
      ) : (
        <div className={"m-auto flex h-full items-center"}>
          <Card
            className={twMerge(
              "bg-white",
              "m-auto flex h-full w-full flex-col p-8 md:h-4/5 md:w-[480px]",
            )}
          >
            <div className={"my-auto flex flex-col justify-center gap-2"}>
              <H2 className={"text-center"}>Reset your password for {email}</H2>
              <InputLabel>New password</InputLabel>
              <InputContainer>
                <InputPassword
                  required={true}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </InputContainer>
              {error && renderErrorMessage()}
              <Button className={"mt-4 w-full"} onClick={handleResetPassword}>
                Save
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
